/**
 * SocialShareModal.tsx - Social Media Sharing Interface Modal
 *
 * This component provides a comprehensive social media sharing interface
 * for fundraisers with platform-specific optimizations and link generation.
 *
 * Key Features:
 * - Multiple social media platform support
 * - Platform-specific link generation and formatting
 * - Custom message templates for each platform
 * - Link copying and sharing functionality
 * - Promoter-specific link generation
 * - Gallery and fundraiser sharing options
 *
 * Supported Platforms:
 * - Facebook with optimized post formatting
 * - Twitter with hashtags and mentions
 * - LinkedIn for professional networks
 * - Instagram with story-friendly content
 * - Email with formatted templates
 * - Direct link copying
 *
 * Props:
 * - isVisible: boolean - Modal visibility state
 * - onClose: () => void - Close modal handler
 *
 * Dependencies: MobX store, social platform APIs, clipboard functionality
 */

import React from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import {
  X,
  Copy,
  Facebook,
  Linkedin,
  Instagram,
  Share2,
  Mail,
} from "lucide-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import { GallerySettingsModel } from "../models/GalleryModels";
import ToastService from "./ToastService";

// Custom X (formerly Twitter) icon component
const XIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const ModalOverlay = styled.div<{ visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: #ffffff;
  border-radius: 24px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: none;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ModalHeader = styled.div`
  padding: 32px 32px 24px;
  text-align: center;
`;

const ModalTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
`;

const ModalSubtitle = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.4;
`;

const SocialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: 0 32px 24px;
`;

const SocialButton = styled.button<{ color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 20px;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${(props) => props.color};

  &:hover {
    border-color: ${(props) => props.color};
    background: ${(props) => props.color}08;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.color}20;
  }

  &:active {
    transform: translateY(0);
  }
`;

const SocialIcon = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SocialLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const LinkSection = styled.div`
  padding: 24px 32px 32px;
  border-top: 1px solid #e2e8f0;
`;

const LinkTitle = styled.h4`
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
`;

const LinkContainer = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #64748b;
  background: #f8fafc;
  font-family: monospace;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`;

const CopyButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// TikTok icon component since it's not in Lucide
const TikTokIcon: React.FC<{ size?: number }> = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

interface SocialShareModalProps {
  isVisible: boolean;
  onClose: () => void;
  gallerySettings: GallerySettingsModel;
  shortUniqueId: string;
}

export const SocialShareModal: React.FC<SocialShareModalProps> = observer(
  ({ isVisible, onClose, gallerySettings, shortUniqueId }) => {
    // Check if we're in gallery context - more comprehensive detection
    const isGalleryContext =
      window.location.pathname.includes("gallery") ||
      window.location.pathname.includes("gallery-settings") ||
      window.location.hash.includes("gallery");

    const selectedFundraiser = fundraiserStore.selectedFundraiser;

    // Check if we're in gallery context
    // Check if we're on a promoter admin page to include promoter ID in shared URL
    //const isPromoterAdminPage = window.location.hash.includes("/promoter/");
    const urlParams = new URLSearchParams(window.location.search);
    const promoterId =
      urlParams.get("PID") || fundraiserStore?.selectedPromoterId || "";

    let shareUrl: string;
    let shareTitle: string;
    let linkSectionTitle: string;

    if (isGalleryContext) {
      shareUrl =
        gallerySettings.pageName && shortUniqueId
          ? fundraiserStore.getPublicGalleryLinkByName(
            shortUniqueId,
            gallerySettings.pageName,
            promoterId
          )
          : fundraiserStore.getGalleryLink(gallerySettings.id, promoterId);
      shareTitle = gallerySettings?.title || "Gallery";
      linkSectionTitle = "Gallery Link";
    } else {
      shareUrl = fundraiserStore.getFundraiserPublicLinkFromModel(
        selectedFundraiser,
        promoterId || ""
      );
      shareTitle = `I wanted to share this important fundraiser with you: ${selectedFundraiser.name || ''}`
      linkSectionTitle = "Fundraiser Link";
    }

    // Truncate URL if too long for display
    const truncateUrl = (url: string, maxLength: number = 50) => {
      if (url.length <= maxLength) return url;
      return url.substring(0, maxLength - 3) + "...";
    };
    const handleOverlayClick = (e: React.MouseEvent) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    React.useEffect(() => {
      if (isVisible) {
        document.addEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "unset";
      }

      return () => {
        document.removeEventListener("keydown", handleKeyDown);
        document.body.style.overflow = "unset";
      };
    }, [isVisible]);

    const copyToClipboard = async (text: string) => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
    };

    const handleCopyLink = async () => {
      await copyToClipboard(shareUrl);
      ToastService.showSuccess(
        `${isGalleryContext ? "Gallery" : "Fundraiser"} link copied to clipboard`
      );
    };

    const handleFacebookShare = () => {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
      window.open(url, "_blank", "width=600,height=400");
    };

    const handleTwitterShare = () => {
      const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`;
      window.open(url, "_blank", "width=600,height=400");
    };

    const handleLinkedInShare = () => {
      const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
      window.open(url, "_blank", "width=600,height=400");
    };

    const handleInstagramShare = () => {
      // Instagram doesn't have a direct sharing URL, so we'll copy the link
      copyToClipboard(shareUrl);
      ToastService.showSuccess(
        "Link copied! Paste it into your Instagram story or bio"
      );
    };

    const handleTikTokShare = () => {
      // TikTok doesn't have a direct sharing URL, so we'll copy the link
      copyToClipboard(shareUrl);
      ToastService.showSuccess(
        "Link copied! Paste it into your TikTok bio or video description"
      );
    };

    const handleEmailShare = () => {
      const subject = encodeURIComponent(`Support ${shareTitle}`);
      const body = encodeURIComponent(
        `I wanted to share this important fundraiser with you: ${shareTitle}\n\n${shareUrl}`
      );
      const url = `mailto:?subject=${subject}&body=${body}`;
      window.open(url);
    };

    const socialPlatforms = [
      {
        name: "Facebook",
        icon: <Facebook size={24} />,
        color: "#1877F2",
        onClick: handleFacebookShare,
      },
      {
        name: "X",
        icon: <XIcon size={24} />,
        color: "#000000",
        onClick: handleTwitterShare,
      },
      {
        name: "LinkedIn",
        icon: <Linkedin size={24} />,
        color: "#0A66C2",
        onClick: handleLinkedInShare,
      },
      {
        name: "Instagram",
        icon: <Instagram size={24} />,
        color: "#E4405F",
        onClick: handleInstagramShare,
      },
      {
        name: "TikTok",
        icon: <TikTokIcon size={24} />,
        color: "#000000",
        onClick: handleTikTokShare,
      },
      {
        name: "Email",
        icon: <Mail size={24} />,
        color: "#34D399",
        onClick: handleEmailShare,
      },
    ];

    return (
      <>
        <ModalOverlay visible={isVisible} onClick={handleOverlayClick}>
          <ModalContent>
            <CloseButton onClick={onClose}>
              <X size={20} />
            </CloseButton>

            <ModalHeader>
              <ModalTitle>
                {isGalleryContext ? "Share Gallery" : "Share Fundraiser"}
              </ModalTitle>
              <ModalSubtitle>
                {isGalleryContext
                  ? "Share the gallery on social media to help spread the word"
                  : "Share this fundraiser on social media to help spread the word"}
              </ModalSubtitle>
            </ModalHeader>

            <SocialGrid>
              {socialPlatforms.map((platform) => (
                <SocialButton
                  key={platform.name}
                  color={platform.color}
                  onClick={platform.onClick}
                >
                  <SocialIcon>{platform.icon}</SocialIcon>
                  <SocialLabel>{platform.name}</SocialLabel>
                </SocialButton>
              ))}
            </SocialGrid>

            <LinkSection>
              <LinkTitle>{linkSectionTitle}</LinkTitle>
              <LinkContainer>
                <LinkInput
                  type="text"
                  value={shareUrl}
                  title={shareUrl}
                  readOnly
                />
                <CopyButton onClick={handleCopyLink}>
                  <Copy size={16} />
                  Copy
                </CopyButton>
              </LinkContainer>
            </LinkSection>
          </ModalContent>
        </ModalOverlay>
      </>
    );
  }
);
